import { cleanInput } from '@exodus/basic-utils';
import { formatAssetAmount } from '@exodus/formatting/lib/asset';
import { formatPrice } from '@exodus/formatting/lib/price';
import { useSelectors } from '@exodus/headless-react';
import useDebouncedCallback from '@exodus/react-hooks/common/useDebouncedCallback';
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import useFiatMode from '../../hooks/use-fiat-mode.js';
import useSwapMode from '../../hooks/use-swap-mode.js';
import { INPUT_MODE } from '../../constants.js';
import useTrackAssetConfigureEvent from '../../hooks/analytics/use-track-configure-asset.js';
import { useQuoteContext } from '../../context/quote.js';
import useToAmount from '../../hooks/use-to-amount.js';
import useFromAmount from '../../hooks/use-from-amount.js';
const formatValue = (value) => formatAssetAmount(value, { useGrouping: false });
const AssetInput = ({ Component, className, style, mode = INPUT_MODE.CRYPTO, direction, asset, amount, fiatAmount, setFiatAmount, setAmount, setPrefilledAmountId, loading, disabled, resetAmount, }, ref) => {
    const componentRef = React.useRef(null);
    useImperativeHandle(ref, () => {
        return {
            focus: componentRef.current?.focus,
            blur: componentRef.current?.blur,
        };
    }, [componentRef]);
    const selectors = useSelectors();
    const trackAssetConfigureSwap = useTrackAssetConfigureEvent();
    const currency = useSelector(selectors.locale.currency);
    const [fiatMode] = useFiatMode();
    const isFiat = mode === INPUT_MODE.FIAT || fiatMode;
    const [swapMode, setSwapMode] = useSwapMode();
    const [isFocused, setIsFocused] = useState(false);
    const [valueWhileFocused, setValueWhileFocused] = useState(amount ? formatValue(amount) : '');
    const _value = isFiat ? fiatAmount : amount;
    const value = _value?.isPositive ? _value : undefined;
    const [toAmount] = useToAmount();
    const [fromAmount] = useFromAmount();
    const enterInputAmount = swapMode === 'ExactIn' ? fromAmount : toAmount;
    useEffect(() => {
        if (enterInputAmount.isZero) {
            resetAmount();
        }
    }, [enterInputAmount.isZero, resetAmount]);
    useEffect(() => {
        if (isFiat) {
            setValueWhileFocused(value ? formatPrice(value.toDefaultNumber(), { currency }) : '');
        }
        else {
            setValueWhileFocused(value ? formatValue(value) : '');
        }
    }, [isFiat, isFocused]);
    const debouncedSet = useDebouncedCallback((value) => {
        if (isFiat) {
            setFiatAmount(value);
        }
        else {
            setAmount(value);
        }
    }, 200);
    const handleChangeText = (text) => {
        void trackAssetConfigureSwap();
        setSwapMode(direction === 'from' ? 'ExactIn' : 'ExactOut');
        setPrefilledAmountId(null);
        if (isFocused) {
            setValueWhileFocused(text);
        }
        debouncedSet(text);
    };
    const handleBlur = () => setIsFocused(false);
    const handleFocus = () => setIsFocused(true);
    const inputValue = isFocused
        ? cleanInput(valueWhileFocused)
        : value?.isPositive
            ? isFiat
                ? formatPrice(value.toDefaultNumber(), {
                    currency,
                    useGrouping: false,
                    format: '%v',
                    convertDust: false,
                })
                : formatValue(value)
            : '';
    const quote = useQuoteContext();
    const quoteAmount = quote.value?.amount;
    const calculateToAmount = useCallback(({ quoteAmount, swapMode }) => {
        if ((swapMode === 'ExactIn' && direction === 'to') ||
            (swapMode === 'ExactOut' && direction === 'from')) {
            if (!quoteAmount) {
                return;
            }
            setAmount(quoteAmount.toDefaultString());
        }
    }, [direction, setAmount]);
    useEffect(() => {
        calculateToAmount({ quoteAmount, swapMode });
    }, [calculateToAmount, quoteAmount, swapMode]);
    if (Component) {
        return (React.createElement(Component, { ref: componentRef, value: inputValue, placeholder: isFiat ? formatPrice(0, { currency }) : '0', asset: asset, loading: loading, className: className, style: style, onChangeText: handleChangeText, onFocus: handleFocus, onBlur: handleBlur, fiatMode: isFiat, disabled: disabled }));
    }
    return null;
};
export default React.forwardRef(AssetInput);
